import { yupResolver } from "@hookform/resolvers/yup"
import pluralize from "pluralize"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { useMutation } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import TelescopeCtaModal from "src/main/TransientRates/TelescopeCtaModal"

import AlertBanner from "src/components/AlertBanner"
import Button from "src/components/Button"
import Form from "src/components/Form"
import Tooltip from "src/components/Tooltip"

import { createRateStrategy, updateRateStrategy } from "src/api/TransientRates"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { snakeCaseToHumanize } from "src/utils/string_helpers"

import RateModifiers from "../RateModifiers"
import { getSchema } from "./schemas"

const RateStrategyForm = ({
  rateStrategy,
  seasonId,
  rateTypes,
  timeframes,
  selectedTimeframe,
  action,
  actionUrl,
  cancelUrl,
  telescopeEnabled,
  telescopeCtaUrl,
  occupancyParams,
}) => {
  const convertedModifiers = rateStrategy.modifiers.map((modifier) => {
    const { percentageAmount, fixedAmount, type } = modifier
    return {
      ...modifier,
      fixedAmount:
        type === "TransientRate::Modifier::Beam" ||
        type === "TransientRate::Modifier::MinimumPrice"
          ? fixedAmount
            ? fixedAmount / 100
            : null
          : modifier.fixedAmount,
      percentageAmount: percentageAmount ? percentageAmount * 100 : null,
    }
  })

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modifiers, setModifiers] = useState(convertedModifiers)
  const showToast = useToast()
  const tracker = useTracker()
  const { currencySymbol } = useMarina()

  const trackingBaseProps = {
    season_id: seasonId,
    ...(rateStrategy.id && { rate_strategy_id: rateStrategy.id }),
  }

  // Track parent-child relationships explicitly, independent of LOA values
  const [parentChildRelationships, setParentChildRelationships] = useState(
    () => {
      // Initialize relationships based on current data structure
      const relationships = {}

      if (rateStrategy.advancedPricing && rateStrategy.rates) {
        // First pass: identify all unique LOA ranges and their parent indices
        const loaToParentIndex = {}

        rateStrategy.rates.forEach((rate, index) => {
          const currentLOA = `${rate.minLength}-${rate.maxLength}`

          // If this LOA hasn't been seen before, this is the parent
          if (
            !Object.prototype.hasOwnProperty.call(loaToParentIndex, currentLOA)
          ) {
            loaToParentIndex[currentLOA] = index
          }
        })

        // Second pass: assign children to their parents
        rateStrategy.rates.forEach((rate, index) => {
          const currentLOA = `${rate.minLength}-${rate.maxLength}`
          const parentIndex = loaToParentIndex[currentLOA]

          // If this row is not the parent but has the same LOA and has beam data, it's a child
          if (
            index !== parentIndex &&
            rate.minBeam !== null &&
            rate.minBeam !== undefined &&
            rate.minBeam !== ""
          ) {
            if (!relationships[parentIndex]) {
              relationships[parentIndex] = []
            }
            relationships[parentIndex].push(index)
          }
        })
      }

      return relationships
    }
  )

  // Use a ref to ensure validation always has access to current relationships
  const parentChildRelationshipsRef = useRef(parentChildRelationships)

  // Update ref whenever relationships change
  useEffect(() => {
    parentChildRelationshipsRef.current = parentChildRelationships
  }, [parentChildRelationships])

  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    context: {
      getParentChildRelationships: () => parentChildRelationshipsRef.current,
    },
    resolver: (values, context, options) => {
      const currentAdvancedPricing =
        values?.rateStrategy?.advancedPricing ?? rateStrategy.advancedPricing

      const schema = getSchema(
        currentAdvancedPricing,
        context.getParentChildRelationships
      )

      return yupResolver(schema)(values, context, options)
    },
    defaultValues: {
      rateStrategy: {
        ...rateStrategy,
        advancedPricing: rateStrategy.advancedPricing,
      },
      rateThresholds: rateStrategy.rates.map((rate) => rate.rateThresholds),
      thresholds: rateStrategy.rates[0]?.rateThresholds?.map(
        ({ threshold }) => threshold
      ),
    },
  })

  // Trigger validation when parentChildRelationships changes
  // Only if there are already errors to avoid premature validation
  useEffect(() => {
    // Only re-validate if there are existing errors in the rates section
    if (Object.keys(errors).length > 0 && errors.rateStrategy?.rates) {
      trigger("rateStrategy.rates")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentChildRelationships, trigger])

  const rateFields = useFieldArray({
    control,
    name: "rateStrategy.rates",
  })

  const rateThresholdFields = useFieldArray({
    control,
    name: "rateThresholds",
  })

  const thresholdColFields = useFieldArray({
    control,
    name: "thresholds",
  })

  const watchedAdvancedPricing = watch("rateStrategy.advancedPricing")
  const watchedRateType = watch("rateStrategy.rateType")

  // Watch specific parent LOA fields to trigger sync
  const parentIndices = Object.keys(parentChildRelationships)
  const watchedParentFields = parentIndices.map((parentIndex) =>
    watch([
      `rateStrategy.rates.${parentIndex}.minLength`,
      `rateStrategy.rates.${parentIndex}.maxLength`,
      `rateStrategy.rates.${parentIndex}.rateTypeOverride`,
    ])
  )

  // Sync LOA values using our tracked relationships
  useEffect(() => {
    if (!watchedAdvancedPricing || !parentChildRelationships) return

    Object.entries(parentChildRelationships).forEach(
      ([parentIndex, childIndices]) => {
        // Use getValues() to get current form values
        const parentRate = getValues(`rateStrategy.rates.${parentIndex}`)
        if (!parentRate) return

        childIndices.forEach((childIndex) => {
          const childRate = getValues(`rateStrategy.rates.${childIndex}`)
          if (!childRate) return

          const parentMinLength = parentRate.minLength ?? ""
          const parentMaxLength = parentRate.maxLength ?? ""

          // Always sync to match parent, regardless of current child values
          setValue(
            `rateStrategy.rates.${childIndex}.minLength`,
            parentMinLength,
            { shouldValidate: false }
          )
          setValue(
            `rateStrategy.rates.${childIndex}.maxLength`,
            parentMaxLength,
            { shouldValidate: false }
          )

          // Also sync rate type override from parent to child
          const parentRateTypeOverride = parentRate.rateTypeOverride ?? ""
          setValue(
            `rateStrategy.rates.${childIndex}.rateTypeOverride`,
            parentRateTypeOverride,
            { shouldValidate: false }
          )
        })
      }
    )
  }, [
    watchedParentFields,
    watchedAdvancedPricing,
    setValue,
    parentChildRelationships,
    getValues,
  ])

  // Function to check if a row is a beam tier using tracked relationships
  const isBeamTier = (index) => {
    if (!watchedAdvancedPricing || index === 0) return false

    // Check if this index is in any parent's children list
    return Object.values(parentChildRelationships).some((childIndices) =>
      childIndices.includes(index)
    )
  }

  const { mutate, isLoading } = useMutation({
    mutationFn: (data) => {
      if (action === "new") {
        return createRateStrategy({ data, actionUrl })
      } else {
        return updateRateStrategy({ data, actionUrl })
      }
    },
    onSuccess: (options, submittedData) => {
      const activeModifiers = modifiers.filter(
        (m) => m.fixedAmount != null || m.percentageAmount != null
      )
      const modifierTypes = activeModifiers.map((m) => m.label)
      if (action === "new") {
        tracker.trackEvent("transient_rates_v2:rate_strategy_created", {
          ...trackingBaseProps,
          strategy_name: submittedData.rateStrategy?.name,
          timeframe: submittedData.rateStrategy?.timeframe || selectedTimeframe,
          rate_type: submittedData.rateStrategy?.rateType,
          pricing_type: submittedData.rateStrategy?.advancedPricing
            ? "advanced"
            : "simple",
          has_modifiers: activeModifiers.length > 0,
          modifier_types: modifierTypes,
          rate_count: submittedData.rateStrategy?.rates?.length || 0,
        })
      } else {
        tracker.trackEvent("transient_rates_v2:rate_strategy_saved", {
          ...trackingBaseProps,
          strategy_name: submittedData.rateStrategy?.name,
          pricing_type: submittedData.rateStrategy?.advancedPricing
            ? "advanced"
            : "simple",
          has_modifiers: activeModifiers.length > 0,
          modifier_types: modifierTypes,
        })
      }
      window.location.href = options.redirectUrl
    },
    onError: (error) => {
      showToast(error.message, { type: "error" })
    },
  })

  const onSaveButtonClick = (data) => {
    mergeData(data)
    mutate(data)
  }

  const mergeModifierData = (data) => {
    const newData = modifiers.filter(
      (modifier) => modifier.type !== data.modifier.type
    )
    const updatedModifiers = [...newData, data.modifier]
    setModifiers(updatedModifiers)

    const hasValue =
      data.modifier.fixedAmount != null ||
      data.modifier.percentageAmount != null
    if (hasValue) {
      const activeModifiers = updatedModifiers.filter(
        (m) => m.fixedAmount != null || m.percentageAmount != null
      )
      tracker.trackEvent("transient_rates_v2:rate_modifier_added", {
        ...trackingBaseProps,
        modifier_type: data.modifier.label,
        modifier_count: activeModifiers.length,
      })
    }
  }

  const mergeData = (data) => {
    // ----------------------- //
    // We have to keep the data in a slightly flatter format because of how
    // the data is structured in the form via the useFieldArray hook.
    // This method zips the data together in a way that the backend is expecting to save it in.
    // ----------------------- //

    // merge thresholds into rateThresholds
    data.thresholds.forEach((threshold, index) => {
      data.rateThresholds.forEach((rateThresholds) => {
        const rateThreshold = rateThresholds?.[index]
        if (rateThreshold) {
          rateThreshold.threshold = parseInt(threshold)
        }
      })
    })

    // Remove rateThresholds entries where the threshold is no longer in the thresholds array
    data.rateThresholds = data.rateThresholds.map((rateThresholds) =>
      rateThresholds
        ? rateThresholds.filter((rateThreshold) =>
            data.thresholds.includes(rateThreshold.threshold)
          )
        : rateThresholds
    )

    // add rates to be destroyed via AR accepts_nested_attributes_for
    const ratesToDestroy = rateStrategy.rates
      .filter(
        (originalRate) =>
          !data.rateStrategy.rates.some((rate) => originalRate.id === rate.id)
      )
      .map((rate) => {
        rate._destroy = true
        return rate
      })
    data.rateStrategy.rates.push(...ratesToDestroy)
    // merge rateThresholds into rates
    data.rateStrategy.rates.forEach((rate, index) => {
      rate.rateThresholds = data.rateThresholds[index]
    })

    // If beam is not enabled, remove beam fields from all rates
    if (!watchedAdvancedPricing) {
      data.rateStrategy.rates.forEach((rate) => {
        delete rate.minBeam
        delete rate.maxBeam
        delete rate.rateTypeOverride
      })
    } else {
      // Convert empty rate type overrides to null for advanced pricing
      data.rateStrategy.rates.forEach((rate) => {
        if (rate.rateTypeOverride === "") {
          rate.rateTypeOverride = null
        }
      })
    }

    // add modifiers to be destroyed via AR accepts_nested_attributes_for
    const modifiersToDestroy = modifiers
      .filter(
        (modifier) =>
          modifier.id !== null &&
          !modifier.percentageAmount &&
          !modifier.fixedAmount
      )
      .map((mod) => {
        mod._destroy = true
        return mod
      })
    // merge modifiers into rateStrategy.modifiers
    data.rateStrategy.modifiers = modifiers.filter(
      (modifier) => modifier.percentageAmount || modifier.fixedAmount
    )

    data.rateStrategy.modifiers.push(...modifiersToDestroy)

    // If simple flat rate, ensure MinimumLoa modifier is destroyed
    if (data.rateStrategy.rateType === "flat" && !watchedAdvancedPricing) {
      data.rateStrategy.modifiers = data.rateStrategy.modifiers
        .map((mod) => {
          if (mod.type === "TransientRate::Modifier::MinimumLoa") {
            return mod.id ? { ...mod, _destroy: true } : null
          }
          return mod
        })
        .filter(Boolean)
    }
  }

  const addRateRow = () => {
    // Find the last parent row to ensure continuity
    let suggestedMinLength = 0
    let suggestedMaxLength = 30

    if (rateFields.fields.length > 0) {
      // Find the last parent row (not a beam tier)
      let lastParentIndex = -1
      for (let i = rateFields.fields.length - 1; i >= 0; i--) {
        if (!watchedAdvancedPricing || !isBeamTier(i)) {
          lastParentIndex = i
          break
        }
      }

      if (lastParentIndex >= 0) {
        const lastParentRate = getValues(
          `rateStrategy.rates.${lastParentIndex}`
        )
        if (lastParentRate) {
          const lastMaxLength = parseInt(lastParentRate.maxLength) || 0
          const lastMinLength = parseInt(lastParentRate.minLength) || 0
          const rangeSize = lastMaxLength - lastMinLength || 30

          // Continue from where the last parent ended
          suggestedMinLength = lastMaxLength
          suggestedMaxLength = lastMaxLength + rangeSize
        }
      }
    }

    const newRate = {
      minLength: suggestedMinLength,
      maxLength: suggestedMaxLength,
      rate: "",
    }

    if (watchedAdvancedPricing) {
      // Parent rows start with complete beam range from 0
      newRate.minBeam = 0
      newRate.maxBeam = 20
      // Initialize rate type override as empty (will use default)
      newRate.rateTypeOverride = ""
    }

    rateFields.append(newRate)
    rateThresholdFields.append([emptyRateThresholdsRow()])
  }

  const removeRateRow = (index) => {
    const isBeamTierRow = isBeamTier(index)
    const childIndices = parentChildRelationships[index] || []

    let confirmMessage = "Are you sure you want to delete this rate row?"

    if (!isBeamTierRow && childIndices.length > 0) {
      confirmMessage = `Are you sure you want to delete this rate row? This will also delete ${
        childIndices.length
      } beam tier ${pluralize("row", childIndices.length)} associated with it.`
    }

    if (window.confirm(confirmMessage)) {
      // If this is a parent row with children, remove all children first (in reverse order)
      if (!isBeamTierRow && childIndices.length > 0) {
        // Sort children in descending order to avoid index shifting issues
        const sortedChildIndices = [...childIndices].sort((a, b) => b - a)

        sortedChildIndices.forEach((childIndex) => {
          rateFields.remove(childIndex)
          rateThresholdFields.remove(childIndex)
        })
      }

      // Now remove the target row (adjust index if children were removed before it)
      let adjustedIndex = index
      if (!isBeamTierRow && childIndices.length > 0) {
        // Count how many children were removed before this index
        const childrenRemovedBefore = childIndices.filter(
          (childIdx) => childIdx < index
        ).length
        adjustedIndex = index - childrenRemovedBefore
      }

      // Remove the target row
      rateFields.remove(adjustedIndex)
      rateThresholdFields.remove(adjustedIndex)

      // Clean up relationships - remove the entire parent entry and adjust remaining indices
      setParentChildRelationships((prev) => {
        const newRelationships = { ...prev }

        // If we removed a parent, delete its entry
        if (!isBeamTierRow) {
          delete newRelationships[index]
        }

        // Remove this index from any parent's children list (if it was a child)
        Object.keys(newRelationships).forEach((parentIndex) => {
          newRelationships[parentIndex] = newRelationships[parentIndex].filter(
            (childIndex) => childIndex !== index
          )

          // Clean up empty arrays
          if (newRelationships[parentIndex].length === 0) {
            delete newRelationships[parentIndex]
          }
        })

        // Adjust all remaining indices based on total number of rows removed
        const totalRowsRemoved = isBeamTierRow ? 1 : 1 + childIndices.length
        const adjustedRelationships = {}

        Object.entries(newRelationships).forEach(([parentIdx, childIdxs]) => {
          const parentIndex = parseInt(parentIdx)

          // Adjust parent index
          let adjustedParentIdx = parentIndex
          if (parentIndex > index) {
            adjustedParentIdx = parentIndex - totalRowsRemoved
          }

          // Adjust child indices
          const adjustedChildIndices = childIdxs
            .map((childIdx) => {
              if (childIdx > index) {
                return childIdx - totalRowsRemoved
              }
              return childIdx
            })
            .filter((childIdx) => childIdx >= 0) // Remove any invalid indices

          if (adjustedChildIndices.length > 0 && adjustedParentIdx >= 0) {
            adjustedRelationships[adjustedParentIdx] = adjustedChildIndices
          }
        })

        return adjustedRelationships
      })
    }
  }

  const addThresholdCol = () => {
    if (!telescopeEnabled) {
      setIsModalOpen(true)
    } else {
      let threshold = 20
      if (hasThresholds()) {
        threshold =
          parseInt(
            getValues(`thresholds.${thresholdColFields.fields.length - 1}`)
          ) + 15
      }
      const columnCount = thresholdColFields.fields.length + 1
      thresholdColFields.append(threshold)
      rateThresholdFields.replace(
        getValues("rateThresholds").map((rateThresholds) =>
          Array.from(
            { length: columnCount },
            (_, i) => rateThresholds?.[i] ?? { rate: "" }
          )
        )
      )
      tracker.trackEvent("transient_rates_v2:occupancy_threshold_added", {
        ...trackingBaseProps,
        threshold_count: thresholdColFields.fields.length + 1,
        threshold_percentage: threshold,
      })
    }
  }

  const removeThresholdCol = (index) => {
    if (
      window.confirm(
        "Are you sure you want to delete this threshold? All rates in this threshold column will be removed."
      )
    ) {
      thresholdColFields.remove(index)
      rateThresholdFields.replace(
        getValues("rateThresholds").map((rateThresholds) =>
          (rateThresholds ?? []).filter((_, i) => i !== index)
        )
      )
    }
  }

  const hasThresholds = () => {
    return thresholdColFields.fields.length > 0 && telescopeEnabled
  }

  // A threshold cell with no form value is filled from the load-time
  // defaultValues at the same index, so new rows need explicit empty rates.
  const emptyRateThresholdsRow = () =>
    hasThresholds()
      ? thresholdColFields.fields.map(() => ({ rate: "" }))
      : undefined

  const getUniqueLOATierCount = () => {
    if (!watchedAdvancedPricing) return rateFields.fields.length

    // Get all child indices from parentChildRelationships
    const allChildIndices = new Set()
    Object.values(parentChildRelationships).forEach((childIndices) => {
      childIndices.forEach((childIndex) => {
        allChildIndices.add(childIndex)
      })
    })

    // Count rows that are NOT children (i.e., parent rows)
    let parentCount = 0
    rateFields.fields.forEach((_, index) => {
      if (!allChildIndices.has(index)) {
        parentCount++
      }
    })

    return parentCount
  }

  const shouldShowDeleteButton = (index) => {
    if (!watchedAdvancedPricing) {
      return rateFields.fields.length > 1
    }

    const isBeamTierRow = isBeamTier(index)
    const uniqueLOACount = getUniqueLOATierCount()

    // For beam tier rows, always show delete (they're always deletable)
    if (isBeamTierRow) {
      return true
    }

    // For parent rows, only show delete if there's more than one unique LOA tier
    // OR if this isn't the first row (index 0)
    return uniqueLOACount > 1 || index > 0
  }

  const renderThresholdHeaders = () => {
    if (!hasThresholds()) return null

    return (
      <th className="bg-blue-100/50" colSpan={thresholdColFields.fields.length}>
        Occupancy Rates
        <br />
        {occupancyParams.totalOccupancy > 0 && (
          <small className="-mb-1 -mt-0.5 block text-gray-600">
            Percentage based on {occupancyParams.totalOccupancy}{" "}
            {occupancyParams.label} of occupancy
          </small>
        )}
      </th>
    )
  }

  const renderTelescopeCtaModal = () => {
    return (
      <TelescopeCtaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        ctaUrl={telescopeCtaUrl}
      />
    )
  }

  const renderThresholdHeaderInputs = () => {
    return thresholdColFields.fields.map((field, index) => {
      return (
        <td key={field.id} className="bg-blue-100/50 !align-top">
          <Form.IconTextField
            icon={<span>%</span>}
            position="right"
            {...register(`thresholds.${index}`)}
            hasErrors={!!errors?.thresholds?.[index]}
          />
          {errors?.thresholds?.[index] && (
            <Form.Error>{errors?.thresholds?.[index].message}</Form.Error>
          )}
        </td>
      )
    })
  }

  const renderThresholdFooters = () => {
    if (!hasThresholds()) return null

    return thresholdColFields.fields.map((field, index) => {
      return (
        <td key={field.id} className="bg-blue-50 !align-top">
          <Button
            variant="ghost"
            fullWidth={true}
            onClick={() => removeThresholdCol(index)}
          >
            <i className="fa-solid fa-trash-can" />
          </Button>
        </td>
      )
    })
  }

  const renderRateThresholds = (rateIndex) => {
    if (!hasThresholds()) return null

    return thresholdColFields.fields.map((field, index) => {
      return (
        <td key={field.id} className="bg-blue-50 !align-top">
          <Form.IconTextField
            icon={<span>{currencySymbol}</span>}
            position="left"
            {...register(`rateThresholds.${rateIndex}.${index}.rate`)}
            hasErrors={!!errors?.rateThresholds?.[rateIndex]?.[index]?.rate}
          />
          {errors?.rateThresholds?.[rateIndex]?.[index]?.rate && (
            <Form.Error>
              {errors?.rateThresholds?.[rateIndex]?.[index]?.rate?.message}
            </Form.Error>
          )}
        </td>
      )
    })
  }

  const renderRateStrategyTimeframe = () => {
    const formField = (
      <Form.Select
        id="timeframe"
        name="select"
        disabled={rateStrategy.mappingCount > 0}
        {...register("rateStrategy.timeframe")}
        hasErrors={!!errors?.rateStrategy?.timeframe}
      >
        {timeframes.map((timeframe) => (
          <option
            value={timeframe}
            key={timeframe}
            selected={timeframe === selectedTimeframe}
          >
            {snakeCaseToHumanize(timeframe)}
          </option>
        ))}
      </Form.Select>
    )
    if (rateStrategy.mappingCount > 0) {
      return (
        <Tooltip
          text="Time frame cannot be modified when the rate strategy is in use. Unmap the rate strategy from all calendar days to modify the time frame."
          placement="top"
          variant="dark"
          maxWidth="400px"
        >
          {formField}
        </Tooltip>
      )
    } else {
      return formField
    }
  }

  const renderStandardTableHeaders = () => {
    return (
      <tr>
        <th className="bg-gray-100">
          Min Length
          <br />
          <small className="-mb-1 -mt-0.5 block text-gray-600">
            Greater than or equal to
          </small>
        </th>
        <th className="bg-gray-100">
          Max Length
          <br />
          <small className="-mb-1 -mt-0.5 block text-gray-600">Less than</small>
        </th>
        <th className="bg-gray-100">
          Rate
          <br />
          <small className="-mb-1 -mt-0.5 block text-gray-600">Per night</small>
        </th>
        {renderThresholdHeaders()}
        <th className="bg-gray-100">
          {!hasThresholds() && (
            <div className="mt-2">
              <Button onClick={addThresholdCol}>{`${
                telescopeEnabled ? "Add threshold" : "Enable Telescope"
              } →`}</Button>
            </div>
          )}
        </th>
      </tr>
    )
  }

  const renderAdvancedPricingTableHeaders = () => {
    return (
      <tr>
        <th className="bg-gray-100">
          Min Length
          <br />
          <small className="-mb-1 -mt-0.5 block text-gray-600">
            Greater than or equal to
          </small>
        </th>
        <th className="bg-gray-100">
          Max Length
          <br />
          <small className="-mb-1 -mt-0.5 block text-gray-600">Less than</small>
        </th>
        <th className="bg-gray-100">
          Min Beam
          <br />
          <small className="-mb-1 -mt-0.5 block text-gray-600">
            Greater than or equal to
          </small>
        </th>
        <th className="bg-gray-100">
          Max Beam
          <br />
          <small className="-mb-1 -mt-0.5 block text-gray-600">Less than</small>
        </th>
        <th className="bg-gray-100">
          Add Beam
          <br />
        </th>
        <th className="bg-gray-100">
          Rate Type
          <br />
          <small className="-mb-1 -mt-0.5 block text-gray-600">
            Override default
          </small>
        </th>
        <th className="bg-gray-100">
          Rate
          <br />
          <small className="-mb-1 -mt-0.5 block text-gray-600">
            Nightly rate
          </small>
        </th>
        {renderThresholdHeaders()}
        <th className="bg-gray-100">
          {!hasThresholds() && (
            <div className="mt-2">
              <Button onClick={addThresholdCol}>{`${
                telescopeEnabled ? "Add threshold" : "Enable Telescope"
              } →`}</Button>
            </div>
          )}
        </th>
      </tr>
    )
  }

  const renderTableHeaders = () => {
    return watchedAdvancedPricing
      ? renderAdvancedPricingTableHeaders()
      : renderStandardTableHeaders()
  }

  const renderStandardTable = () => {
    const html = []

    if (hasThresholds()) {
      html.push(
        <tr key="header">
          <td className="!align-top" colSpan="2"></td>
          <td className="!align-top">
            <Form.IconTextField
              value="0"
              icon={<span>%</span>}
              position="right"
              disabled
            />
          </td>
          {renderThresholdHeaderInputs()}
          <td className="!align-top">
            <Button onClick={addThresholdCol}>{`${
              telescopeEnabled ? "Add threshold" : "Enable Telescope"
            } →`}</Button>
          </td>
        </tr>
      )
    }

    rateFields.fields.forEach((field, index) => {
      html.push(
        <tr key={field.id} className={isBeamTier(index) ? "bg-blue-50" : ""}>
          <td className="!align-top">
            <Form.IconTextField
              icon={<span>ft</span>}
              position="right"
              {...register(`rateStrategy.rates.${index}.minLength`, {
                valueAsNumber: true,
              })}
              hasErrors={
                !!errors?.rateStrategy?.rates?.[index]?.minLength ||
                !!errors?.rateStrategy?.rates?.message
              }
            />
            {errors?.rateStrategy?.rates?.[index]?.minLength && (
              <Form.Error>
                {errors?.rateStrategy?.rates?.[index]?.minLength.message}
              </Form.Error>
            )}
          </td>
          <td className="!align-top">
            <Form.IconTextField
              icon={<span>ft</span>}
              position="right"
              {...register(`rateStrategy.rates.${index}.maxLength`, {
                valueAsNumber: true,
              })}
              hasErrors={!!errors?.rateStrategy?.rates?.[index]?.maxLength}
            />
            {errors?.rateStrategy?.rates?.[index]?.maxLength && (
              <Form.Error>
                {errors?.rateStrategy?.rates?.[index]?.maxLength.message}
              </Form.Error>
            )}
          </td>
          <td className="!align-top">
            <Form.IconTextField
              icon={<span>{currencySymbol}</span>}
              position="left"
              {...register(`rateStrategy.rates.${index}.rate`)}
              hasErrors={!!errors?.rateStrategy?.rates?.[index]?.rate}
            />
            {errors?.rateStrategy?.rates?.[index]?.rate && (
              <Form.Error>
                {errors?.rateStrategy?.rates?.[index]?.rate.message}
              </Form.Error>
            )}
          </td>
          {renderRateThresholds(index)}
          <td className="!align-top">
            {shouldShowDeleteButton(index) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeRateRow(index)}
              >
                <i className="fa-solid fa-trash-can text-red-500" />
              </Button>
            )}
          </td>
        </tr>
      )
    })

    if (hasThresholds()) {
      html.push(
        <tr key="footer">
          <td className="!align-top">
            <Button onClick={addRateRow}>Add rate row ↓</Button>
          </td>
          <td colSpan="2" className="!align-top"></td>
          {renderThresholdFooters()}
        </tr>
      )
    } else {
      html.push(
        <tr key="footer">
          <td colSpan="100" className="!align-top">
            <Button onClick={addRateRow}>Add rate row ↓</Button>
          </td>
        </tr>
      )
    }

    return html
  }

  const renderAdvancedPricingTable = () => {
    const html = []

    if (hasThresholds()) {
      html.push(
        <tr key="header">
          <td className="!align-top" colSpan="6"></td>
          <td className="!align-top">
            <Form.IconTextField
              value="0"
              icon={<span>%</span>}
              position="right"
              disabled
            />
          </td>
          {renderThresholdHeaderInputs()}
          <td className="!align-top">
            <Button onClick={addThresholdCol}>{`${
              telescopeEnabled ? "Add threshold" : "Enable Telescope"
            } →`}</Button>
          </td>
        </tr>
      )
    }

    rateFields.fields.forEach((field, index) => {
      const isBeamTierRow = isBeamTier(index)

      html.push(
        <tr key={field.id} className={isBeamTierRow ? "bg-blue-50" : ""}>
          <td className={isBeamTierRow ? "bg-blue-50" : ""}>
            {isBeamTierRow ? (
              <div className="flex items-center justify-center py-2">
                <div className="flex items-center text-blue-600">
                  <span className="mr-2 text-xl font-semibold">⤷</span>
                </div>
                <input
                  type="hidden"
                  {...register(`rateStrategy.rates.${index}.minLength`, {
                    valueAsNumber: true,
                  })}
                />
              </div>
            ) : (
              <Form.IconTextField
                icon={<span>ft</span>}
                position="right"
                {...register(`rateStrategy.rates.${index}.minLength`, {
                  valueAsNumber: true,
                })}
                hasErrors={
                  !!errors?.rateStrategy?.rates?.[index]?.minLength ||
                  !!errors?.rateStrategy?.rates?.message
                }
              />
            )}
            {!isBeamTierRow &&
              errors?.rateStrategy?.rates?.[index]?.minLength && (
                <Form.Error>
                  {errors?.rateStrategy?.rates?.[index]?.minLength.message}
                </Form.Error>
              )}
          </td>
          <td className={isBeamTierRow ? "bg-blue-50" : ""}>
            {isBeamTierRow ? (
              <div className="flex items-center justify-center py-2">
                <input
                  type="hidden"
                  {...register(`rateStrategy.rates.${index}.maxLength`, {
                    valueAsNumber: true,
                  })}
                />
              </div>
            ) : (
              <Form.IconTextField
                icon={<span>ft</span>}
                position="right"
                {...register(`rateStrategy.rates.${index}.maxLength`, {
                  valueAsNumber: true,
                })}
                hasErrors={!!errors?.rateStrategy?.rates?.[index]?.maxLength}
              />
            )}
            {!isBeamTierRow &&
              errors?.rateStrategy?.rates?.[index]?.maxLength && (
                <Form.Error>
                  {errors?.rateStrategy?.rates?.[index]?.maxLength.message}
                </Form.Error>
              )}
          </td>
          <td className={isBeamTierRow ? "bg-blue-50" : ""}>
            <Form.IconTextField
              icon={<span>ft</span>}
              position="right"
              {...register(`rateStrategy.rates.${index}.minBeam`, {
                valueAsNumber: true,
              })}
              hasErrors={!!errors?.rateStrategy?.rates?.[index]?.minBeam}
            />
            {errors?.rateStrategy?.rates?.[index]?.minBeam && (
              <Form.Error>
                {errors?.rateStrategy?.rates?.[index]?.minBeam.message}
              </Form.Error>
            )}
          </td>
          <td className={isBeamTierRow ? "bg-blue-50" : ""}>
            <Form.IconTextField
              icon={<span>ft</span>}
              position="right"
              {...register(`rateStrategy.rates.${index}.maxBeam`, {
                valueAsNumber: true,
              })}
              hasErrors={!!errors?.rateStrategy?.rates?.[index]?.maxBeam}
            />
            {errors?.rateStrategy?.rates?.[index]?.maxBeam && (
              <Form.Error>
                {errors?.rateStrategy?.rates?.[index]?.maxBeam.message}
              </Form.Error>
            )}
          </td>
          <td className={isBeamTierRow ? "bg-blue-50" : ""}>
            {!isBeamTierRow && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => addBeamRow(index)}
              >
                <i className="fa-solid fa-plus" /> Beam
              </Button>
            )}
          </td>
          <td className={isBeamTierRow ? "bg-blue-50" : ""}>
            {!isBeamTierRow && (
              <Form.Select
                {...register(`rateStrategy.rates.${index}.rateTypeOverride`)}
                hasErrors={
                  !!errors?.rateStrategy?.rates?.[index]?.rateTypeOverride
                }
              >
                <option value="">
                  Use default
                  {getValues("rateStrategy.rateType")
                    ? ` (${snakeCaseToHumanize(
                        getValues("rateStrategy.rateType")
                      )})`
                    : ""}
                </option>
                {rateTypes.map((rateType) => (
                  <option value={rateType} key={rateType}>
                    {snakeCaseToHumanize(rateType)}
                  </option>
                ))}
              </Form.Select>
            )}
            {!isBeamTierRow &&
              errors?.rateStrategy?.rates?.[index]?.rateTypeOverride && (
                <Form.Error>
                  {
                    errors?.rateStrategy?.rates?.[index]?.rateTypeOverride
                      .message
                  }
                </Form.Error>
              )}
          </td>
          <td className={isBeamTierRow ? "bg-blue-50" : ""}>
            <Form.IconTextField
              icon={<span>{currencySymbol}</span>}
              position="left"
              {...register(`rateStrategy.rates.${index}.rate`)}
              hasErrors={!!errors?.rateStrategy?.rates?.[index]?.rate}
            />
            {errors?.rateStrategy?.rates?.[index]?.rate && (
              <Form.Error>
                {errors?.rateStrategy?.rates?.[index]?.rate.message}
              </Form.Error>
            )}
          </td>
          {renderRateThresholds(index)}
          <td className={isBeamTierRow ? "bg-blue-50" : ""}>
            {shouldShowDeleteButton(index) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeRateRow(index)}
              >
                <i className="fa-solid fa-trash-can text-red-500" />
              </Button>
            )}
          </td>
        </tr>
      )
    })

    if (hasThresholds()) {
      html.push(
        <tr key="footer">
          <td className="!align-top">
            <Button onClick={addRateRow}>Add rate row ↓</Button>
          </td>
          <td colSpan="6" className="!align-top"></td>
          {renderThresholdFooters()}
        </tr>
      )
    } else {
      html.push(
        <tr key="footer">
          <td colSpan="100" className="!align-top">
            <Button onClick={addRateRow}>Add rate row ↓</Button>
          </td>
        </tr>
      )
    }

    return html
  }

  const renderRateStrategies = () => {
    return watchedAdvancedPricing
      ? renderAdvancedPricingTable()
      : renderStandardTable()
  }

  const addBeamRow = (baseIndex) => {
    if (!watchedAdvancedPricing) return

    const baseRate = getValues(`rateStrategy.rates.${baseIndex}`)

    // Ensure the parent starts with minBeam = 0
    if (
      baseRate.minBeam === "" ||
      baseRate.minBeam === undefined ||
      baseRate.minBeam === null
    ) {
      setValue(`rateStrategy.rates.${baseIndex}.minBeam`, 0, {
        shouldValidate: false,
      })
    }

    // Check if the base row already has beam values
    const baseHasBeamValues =
      baseRate.minBeam !== "" && baseRate.minBeam !== undefined

    // If the parent doesn't have maxBeam set, set it to a default value
    if (!baseRate.maxBeam || baseRate.maxBeam === "") {
      setValue(`rateStrategy.rates.${baseIndex}.maxBeam`, 10, {
        shouldValidate: false,
      })
    }

    // Determine appropriate beam values for the new beam tier
    let suggestedMinBeam = 0
    let suggestedMaxBeam = 10

    // Look for existing beam children of this parent to suggest next logical beam range
    const childrenForInsertion = parentChildRelationships[baseIndex] || []

    if (childrenForInsertion.length > 0) {
      // Find the highest maxBeam among existing children
      let highestMaxBeam = parseInt(baseRate.maxBeam) || 10

      childrenForInsertion.forEach((childIndex) => {
        const childRate = getValues(`rateStrategy.rates.${childIndex}`)
        if (childRate && childRate.maxBeam) {
          const childMaxBeam = parseInt(childRate.maxBeam)
          if (childMaxBeam > highestMaxBeam) {
            highestMaxBeam = childMaxBeam
          }
        }
      })

      suggestedMinBeam = highestMaxBeam
      suggestedMaxBeam = highestMaxBeam + 5
    } else if (baseHasBeamValues) {
      // If base row has beam values, continue from its maxBeam
      suggestedMinBeam = baseRate.maxBeam || 0
      suggestedMaxBeam = baseRate.maxBeam ? parseInt(baseRate.maxBeam) + 5 : 10
    } else {
      // If base row doesn't have beam values, the new child should start where parent ends
      suggestedMinBeam = 10 // Start where parent's maxBeam (10) ends
      suggestedMaxBeam = 15
    }

    const newRate = {
      minLength: baseRate.minLength,
      maxLength: baseRate.maxLength,
      rate: "",
      minBeam: suggestedMinBeam,
      maxBeam: suggestedMaxBeam,
      // Inherit parent's rate type override (beam tiers don't override)
      rateTypeOverride: baseRate.rateTypeOverride || "",
    }

    // Find the position to insert: after the last existing child, or after parent if no children
    let insertionIndex = baseIndex + 1

    if (childrenForInsertion.length > 0) {
      // Insert after the last child
      const lastChildIndex = Math.max(...childrenForInsertion)
      insertionIndex = lastChildIndex + 1
    }

    rateFields.insert(insertionIndex, newRate)
    rateThresholdFields.insert(insertionIndex, [emptyRateThresholdsRow()])

    tracker.trackEvent("transient_rates_v2:beam_range_added", {
      ...trackingBaseProps,
      beam_tier_count: (childrenForInsertion.length || 0) + 1,
      beam_range: `${suggestedMinBeam}-${suggestedMaxBeam}`,
    })

    // Update all relationship indices that are affected by the insertion
    setParentChildRelationships((prev) => {
      const newRelationships = {}

      Object.entries(prev).forEach(([parentIdx, childIndices]) => {
        const parentIndex = parseInt(parentIdx)

        // Adjust parent index if it's after the insertion point
        const adjustedParentIndex =
          parentIndex >= insertionIndex ? parentIndex + 1 : parentIndex

        // Adjust child indices if they're after the insertion point
        const adjustedChildIndices = childIndices.map((childIdx) =>
          childIdx >= insertionIndex ? childIdx + 1 : childIdx
        )

        newRelationships[adjustedParentIndex] = adjustedChildIndices
      })

      // Add the new relationship
      if (!newRelationships[baseIndex]) {
        newRelationships[baseIndex] = []
      }
      newRelationships[baseIndex].push(insertionIndex)

      return newRelationships
    })
  }

  return (
    <div className="card p-4 lg:p-12">
      <div className="space-y-12">
        <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 lg:grid-cols-4">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Rate Strategy Setup
            </h2>
            <div className="mt-1 text-sm text-gray-600">
              This information is the initial configuration for setting up a
              rate. Actual rates are set up below.
              {!telescopeEnabled && (
                <div className="mt-10 rounded-lg bg-blue-50 p-4 xl:p-10">
                  <p className="mb-2 text-xl font-bold text-blue-900 xl:mb-4 xl:text-3xl">
                    Dockwa Telescope
                  </p>
                  <p>
                    Welcome to the future of pricing! Dockwa Telescope empowers
                    you to adjust your rates based on occupancy, optimizing your
                    marina&#39;s performance effortlessly.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="col-span-3 max-w-5xl space-y-8">
            <div>
              <Form.Label htmlFor="strategy-name">Strategy Name</Form.Label>
              <Form.TextField
                id="strategy-name"
                {...register("rateStrategy.name")}
                hasErrors={!!errors?.rateStrategy?.name}
              />
              {errors?.rateStrategy?.name && (
                <Form.Error>{errors?.rateStrategy?.name.message}</Form.Error>
              )}
              <div className="mt-2 text-sm text-gray-600">
                For internal use only. This is the name of the rate strategy.
                (ex. Weekday Rate)
              </div>
            </div>
            <div>
              <Form.Label htmlFor="rate-type">Rate Type</Form.Label>
              <Form.Select
                id="rate-type"
                name="select"
                {...register("rateStrategy.rateType")}
                hasErrors={!!errors?.rateStrategy?.rateType}
              >
                <option value=""></option>
                {rateTypes.map((rateType) => (
                  <option value={rateType} key={rateType}>
                    {snakeCaseToHumanize(rateType)}
                  </option>
                ))}
              </Form.Select>
              {errors?.rateStrategy?.rateType && (
                <Form.Error>
                  {errors?.rateStrategy?.rateType.message}
                </Form.Error>
              )}
              <div className="mt-2 text-sm text-gray-600">
                &quot;By Length&quot; rate is a per foot rate type.
                &quot;Flat&quot; rate is a fixed rate per night/day.
              </div>
            </div>
            <div>
              <Form.Label htmlFor="timeframe">Time Frame</Form.Label>
              {renderRateStrategyTimeframe()}
              {errors?.rateStrategy?.timeframe && (
                <Form.Error>
                  {errors?.rateStrategy?.timeframe.message}
                </Form.Error>
              )}
              <div className="mt-2 text-sm text-gray-600">
                Set the time frame for the rate strategy.
              </div>
            </div>
            <div>
              <Form.Label htmlFor="description">
                Description (optional)
              </Form.Label>
              <Form.Textarea
                id="description"
                {...register("rateStrategy.description")}
              />
              <div className="mt-2 text-sm text-gray-600">
                This field is for internal use only, boaters will not see it.
                Use this space to give context about this rate strategy or how
                it should be used.
              </div>
            </div>
            <div>
              <Form.Label htmlFor="rate-configuration">
                Rate Configuration
              </Form.Label>
              {action === "edit" && rateStrategy.advancedPricing ? (
                <>
                  <Tooltip
                    text="Advanced configurations cannot be changed back to simple. To use a simple rate setup, please create a new rate strategy instead."
                    placement="top"
                    variant="dark"
                    maxWidth="300px"
                  >
                    <Form.Select
                      id="rate-configuration"
                      value={rateStrategy.advancedPricing}
                      disabled={true}
                      hasErrors={!!errors?.rateStrategy?.advancedPricing}
                    >
                      <option value={false}>Simple Configuration</option>
                      <option value={true}>Advanced Configuration</option>
                    </Form.Select>
                  </Tooltip>
                  <input
                    type="hidden"
                    {...register("rateStrategy.advancedPricing")}
                    value={true}
                  />
                </>
              ) : (
                <Form.Select
                  id="rate-configuration"
                  {...register("rateStrategy.advancedPricing", {
                    setValueAs: (value) => value === "true",
                  })}
                  hasErrors={!!errors?.rateStrategy?.advancedPricing}
                >
                  <option value={false}>Simple Configuration</option>
                  <option value={true}>Advanced Configuration</option>
                </Form.Select>
              )}
              {errors?.rateStrategy?.advancedPricing && (
                <Form.Error>
                  {errors?.rateStrategy?.advancedPricing.message}
                </Form.Error>
              )}
              <div className="mt-2 text-sm text-gray-600">
                Simple configuration uses length-based pricing only. Advanced
                configuration allows for beam-based pricing tiers and row-level
                rate type overrides.
              </div>
              {watchedAdvancedPricing && !rateStrategy.advancedPricing && (
                <div className="mt-4">
                  <AlertBanner variant="warning">
                    Advanced configuration is significantly more complex and
                    should only be used when beam-based pricing tiers or
                    row-level rate type overrides are required. Consider using
                    simple configuration unless you specifically need to charge
                    different rates based on boat beam width or rate type.
                  </AlertBanner>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 lg:grid-cols-4">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Rate Setup
            </h2>
            <div className="mt-1 text-sm text-gray-600">
              Configure the rate here. You can add min and max lengths
              {watchedAdvancedPricing ? " and beams" : ""} to apply specific
              rates to different boat sizes.
            </div>
          </div>

          <div className="lg:col-span-3">
            <table className="table border">
              <thead>{renderTableHeaders()}</thead>
              <tbody>{renderRateStrategies()}</tbody>
            </table>
            {errors?.rateStrategy?.rates && (
              <Form.Error>{errors?.rateStrategy?.rates?.message}</Form.Error>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 lg:grid-cols-4">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Modifiers Setup
            </h2>
            <div className="mt-1 text-sm text-gray-600">
              Configure rate modifiers here. Rate modifiers adjust the rate
              based off of the conditions provided.
            </div>
          </div>

          <RateModifiers
            modifiers={modifiers}
            mergeModifierData={mergeModifierData}
            rateType={watchedRateType}
            advancedPricingEnabled={watchedAdvancedPricing}
          />
        </div>

        <div className="flex w-full justify-end pb-12">
          <div className="flex gap-x-4">
            <Button
              variant="primary"
              onClick={() => {
                handleSubmit(onSaveButtonClick)()
              }}
              isLoading={isLoading}
            >
              Save Changes
            </Button>
            <a href={cancelUrl} className="btn btn-secondary">
              Cancel
            </a>
          </div>
        </div>
      </div>
      {renderTelescopeCtaModal()}
    </div>
  )
}

RateStrategyForm.propTypes = {
  rateStrategy: PropTypes.object.isRequired,
  seasonId: PropTypes.number.isRequired,
  rateTypes: PropTypes.array.isRequired,
  timeframes: PropTypes.array.isRequired,
  selectedTimeframe: PropTypes.string.isRequired,
  action: PropTypes.oneOf(["new", "edit"]).isRequired,
  actionUrl: PropTypes.string.isRequired,
  cancelUrl: PropTypes.string.isRequired,
  telescopeEnabled: PropTypes.bool.isRequired,
  telescopeCtaUrl: PropTypes.string.isRequired,
  occupancyParams: PropTypes.object.isRequired,
}

export default RateStrategyForm
